import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ArrowLeftRight } from "lucide-react";
import {
  clampHeightCm,
  cmToFeet,
  feetToCm,
  formatHeight,
} from "./heightUtils";
import { scrollFormFieldIntoView } from "./profile/profileFormUtils";
import { useHeightDisplayUnit } from "./useHeightDisplayUnit";
import { useIsMobile } from "./useIsMobile";

function heightSectionScrollTarget(picker: HTMLElement) {
  const group = picker.closest(".preferences-group") as HTMLElement | null;
  const previousElement = group?.previousElementSibling;

  return previousElement instanceof HTMLElement && previousElement.classList.contains("preference-subhead")
    ? previousElement
    : group ?? picker;
}

export function HeightPicker({
  value = 170,
  onChange,
  label = "Height",
  showToolbarLabel = true,
}: {
  value?: number;
  onChange: (value: number) => void;
  label?: string;
  showToolbarLabel?: boolean;
}) {
  const mobile = useIsMobile();
  const { useImperial, setUseImperial } = useHeightDisplayUnit();
  const [displayCm, setDisplayCm] = useState(clampHeightCm(value));
  const [editing, setEditing] = useState(false);
  const [draftCm, setDraftCm] = useState("");
  const [draftFeet, setDraftFeet] = useState("");
  const [draftInches, setDraftInches] = useState("");

  const cmInputRef = useRef<HTMLInputElement>(null);
  const feetRef = useRef<HTMLInputElement>(null);
  const inchesRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayCm(clampHeightCm(value));
  }, [value]);

  useEffect(() => {
    if (!mobile || !editing) return;

    const viewport = window.visualViewport;
    let scrollFrame: number | undefined;
    let settleTimer: number | undefined;

    const scrollHeightSectionIntoView = () => {
      const picker = pickerRef.current;
      if (picker) {
        scrollFormFieldIntoView(heightSectionScrollTarget(picker), "auto");
      }
    };

    const keepHeightSectionInView = () => {
      if (scrollFrame !== undefined) window.cancelAnimationFrame(scrollFrame);
      window.clearTimeout(settleTimer);

      scrollFrame = window.requestAnimationFrame(scrollHeightSectionIntoView);
      settleTimer = window.setTimeout(scrollHeightSectionIntoView, 240);
    };

    keepHeightSectionInView();
    window.addEventListener("resize", keepHeightSectionInView);
    viewport?.addEventListener("resize", keepHeightSectionInView);
    viewport?.addEventListener("scroll", keepHeightSectionInView);

    return () => {
      if (scrollFrame !== undefined) window.cancelAnimationFrame(scrollFrame);
      window.clearTimeout(settleTimer);
      window.removeEventListener("resize", keepHeightSectionInView);
      viewport?.removeEventListener("resize", keepHeightSectionInView);
      viewport?.removeEventListener("scroll", keepHeightSectionInView);
    };
  }, [editing, mobile]);

  const applyValue = (cm: number) => {
    const next = clampHeightCm(cm);
    setDisplayCm(next);
    onChange(next);
  };

  const focusInput = () => {
    const input = useImperial ? feetRef.current : cmInputRef.current;
    input?.focus();
    input?.select();
  };

  const beginEdit = (immediateFocus = false) => {
    const imperial = cmToFeet(displayCm);
    const applyEditState = () => {
      setEditing(true);
      setDraftCm(String(displayCm));
      setDraftFeet(String(imperial.feet));
      setDraftInches(String(imperial.inches));
    };

    if (immediateFocus) {
      flushSync(applyEditState);
      focusInput();
      return;
    }

    applyEditState();
    requestAnimationFrame(() => {
      focusInput();
    });
  };

  const commitEdit = () => {
    let cm: number | null = null;

    if (useImperial) {
      const feet = Number.parseInt(draftFeet, 10);
      const inches = Number.parseInt(draftInches, 10);
      if (!Number.isNaN(feet) && !Number.isNaN(inches)) {
        const clampedInches = Math.max(0, Math.min(11, inches));
        cm = feetToCm(Math.max(4, Math.min(8, feet)), clampedInches);
      }
    } else {
      const parsed = Number.parseInt(draftCm, 10);
      if (!Number.isNaN(parsed)) cm = parsed;
    }

    if (cm !== null) applyValue(cm);
    setEditing(false);
    setDraftCm("");
    setDraftFeet("");
    setDraftInches("");
  };

  const toggleUnit = () => {
    if (editing) commitEdit();
    setUseImperial(!useImperial);
  };

  const pickerClassName = [
    "height-picker",
    "height-picker--inline",
    mobile ? "" : "height-picker--profile-field",
  ].filter(Boolean).join(" ");

  return (
    <div ref={pickerRef} className={pickerClassName}>
      {showToolbarLabel ? <span className="height-range-unit-label height-picker-stacked-label">{label}</span> : null}
      <div className="height-picker-inline-row">
        <div
          className={`height-picker-value ${editing ? "active" : ""}`}
          onPointerDown={(event) => {
            if (!editing) {
              event.preventDefault();
              beginEdit(true);
            }
          }}
          onClick={() => {
            if (!editing) beginEdit();
          }}
        >
          {editing && useImperial ? (
            <div
              className="height-box-imperial-inputs"
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                  commitEdit();
                }
              }}
            >
              <input
                ref={feetRef}
                className="age-range-box-input height-box-feet"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                enterKeyHint="next"
                aria-label={`${label} feet`}
                maxLength={1}
                value={draftFeet}
                onFocus={() => {
                  if (!editing) beginEdit();
                }}
                onChange={(event) => setDraftFeet(event.target.value.replace(/\D/g, "").slice(0, 1))}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    inchesRef.current?.focus();
                    inchesRef.current?.select();
                  }
                }}
              />
              <span className="height-box-unit-mark" aria-hidden="true">'</span>
              <input
                ref={inchesRef}
                className="age-range-box-input height-box-inches"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                enterKeyHint="done"
                aria-label={`${label} inches`}
                maxLength={2}
                value={draftInches}
                onChange={(event) => setDraftInches(event.target.value.replace(/\D/g, "").slice(0, 2))}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
              />
              <span className="height-box-unit-mark" aria-hidden="true">"</span>
            </div>
          ) : editing ? (
            <div className="height-box-metric-input">
              <input
                ref={cmInputRef}
                className="age-range-box-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                enterKeyHint="done"
                aria-label={label}
                maxLength={3}
                value={draftCm}
                onFocus={() => {
                  if (!editing) beginEdit();
                }}
                onChange={(event) => setDraftCm(event.target.value.replace(/\D/g, "").slice(0, 3))}
                onBlur={commitEdit}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    cmInputRef.current?.blur();
                  }
                }}
              />
              <span className="height-box-unit-mark" aria-hidden="true">cm</span>
            </div>
          ) : (
            <span className="height-picker-display">{formatHeight(displayCm, useImperial)}</span>
          )}
        </div>
        <button
          className="height-range-unit-toggle"
          type="button"
          onClick={toggleUnit}
          aria-label={useImperial ? "Switch to centimeters" : "Switch to feet and inches"}
        >
          {mobile ? (
            <>
              {useImperial ? "Switch to cm" : "Switch to ft/in"}
              <ArrowLeftRight size={16} aria-hidden="true" />
            </>
          ) : (
            useImperial ? "cm" : "ft/in"
          )}
        </button>
      </div>
    </div>
  );
}
